import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { TenantInfo, CurrentTenantInfo } from 'core/types/tenant.type'

// Tenant state interface
interface TenantState {
  tenants: TenantInfo[]
  currentTenant: CurrentTenantInfo | null
  loading: boolean
  error: string | null
  lastFetchTime: number | null // Track when data was last fetched
}

const initialState: TenantState = {
  tenants: [],
  currentTenant: null,
  loading: false,
  error: null,
  lastFetchTime: null,
}

const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
      if (action.payload) {
        state.error = null
      }
    },

    // Set tenants data
    setTenants: (state, action: PayloadAction<TenantInfo[]>) => {
      state.tenants = action.payload
      state.loading = false
      state.error = null
      state.lastFetchTime = Date.now()
    },

    // Set current tenant
    setCurrentTenant: (state, action: PayloadAction<CurrentTenantInfo>) => {
      state.currentTenant = action.payload
    },

    // Set error
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.loading = false
    },

    // Clear all tenant data
    clearTenants: (state) => {
      state.tenants = []
      state.currentTenant = null
      state.error = null
      state.loading = false
      state.lastFetchTime = null
    },

    // Update tenant in list (e.g., after role change)
    updateTenant: (state, action: PayloadAction<TenantInfo>) => {
      const index = state.tenants.findIndex(
        (item) => item.tenant?.strid === action.payload.tenant?.strid
      )
      if (index !== -1) {
        state.tenants[index] = action.payload
      }

      // Also update current tenant if it matches
      if (state.currentTenant?.tenant_strid === action.payload.tenant.strid) {
        state.currentTenant = {
          tenant_strid: action.payload.tenant.strid,
          tenant_name: action.payload.tenant.name,
          tenant_domain: action.payload.tenant.domain,
          tenant_role: action.payload.user.tenant_role as 'ADMIN' | 'MEMBER',
          user_profile: action.payload.user,
        }
      }
    },
  },
})

export const {
  setLoading,
  setTenants,
  setCurrentTenant,
  setError,
  clearTenants,
  updateTenant,
} = tenantSlice.actions

export default tenantSlice.reducer
